/**
 * ============================================================================
 * MAPEAMENTO CENTRALIZADO DE CULTURAS
 * ============================================================================
 * 
 * Este arquivo define o padrão de culturas usado em todo o sistema:
 * - Dropdown de cadastro de talhões
 * - Busca de dados ZARC (Zoneamento Agrícola)
 * - Fenologia cultural
 * - Outras funcionalidades que dependem do nome da cultura
 * 
 * PADRÃO: Seguimos os nomes do ZARC (fonte oficial do MAPA)
 */

// ============================================================================
// CULTURAS PRINCIPAIS (ZARC)
// ============================================================================

/**
 * Lista de culturas disponíveis para seleção
 * Formato: { value: "nome_zarc", label: "Nome exibido", fenologia: "chave_fenologia" }
 */
export const CULTURAS_DISPONIVEIS = [
  {
    value: 'Soja',
    label: 'Soja',
    fenologia: 'soja',
    zarc: true
  },
  {
    value: 'Milho',
    label: 'Milho',
    fenologia: 'milho',
    zarc: true,
    observacao: 'Inclui 1ª e 2ª safra'
  },
  {
    value: 'Feijão',
    label: 'Feijão',
    fenologia: 'feijao',
    zarc: true,
    observacao: 'Inclui 1ª, 2ª e 3ª safra'
  },
  {
    value: 'Café',
    label: 'Café Arábica',
    fenologia: 'cafe',
    zarc: true,
    observacao: 'Arábica'
  },
  {
    value: 'Café Conilon',
    label: 'Café Conilon',
    fenologia: 'cafe_conilon',
    zarc: true,
    observacao: 'Conilon / Robústa'
  },
  {
    value: 'Algodão',
    label: 'Algodão',
    fenologia: 'algodao',
    zarc: true
  },
  {
    value: 'Trigo',
    label: 'Trigo',
    fenologia: 'trigo',
    zarc: true
  },
  {
    value: 'Arroz',
    label: 'Arroz',
    fenologia: 'arroz',
    zarc: true
  },
  {
    value: 'Cana-de-açúcar',
    label: 'Cana-de-açúcar',
    fenologia: 'cana_de_acucar',
    zarc: true
  },
  {
    value: 'Sorgo',
    label: 'Sorgo',
    fenologia: 'sorgo',
    zarc: true
  },
  {
    value: 'Girassol',
    label: 'Girassol',
    fenologia: 'girassol',
    zarc: true
  },
  {
    value: 'Amendoim',
    label: 'Amendoim',
    fenologia: 'amendoim',
    zarc: true
  },
  {
    value: 'Mandioca',
    label: 'Mandioca',
    fenologia: 'mandioca',
    zarc: true
  },
  {
    value: 'Eucalipto',
    label: 'Eucalipto',
    fenologia: 'eucalipto',
    zarc: false,
    observacao: 'Ciclo de 5-7 anos'
  },
  {
    value: 'Tomate',
    label: 'Tomate',
    fenologia: 'tomate',
    zarc: true,
    aliases: ['Tomate industrial'],
    observacao: 'ZARC disponível para tomate industrial'
  },
  {
    value: 'Batata',
    label: 'Batata',
    fenologia: 'batata',
    zarc: true,
    aliases: ['Batata Mesa', 'Batata Indústria'],
    observacao: 'ZARC disponível para mesa e indústria'
  },
  {
    value: 'Cebola',
    label: 'Cebola',
    fenologia: 'cebola',
    zarc: true,
    aliases: ['Cebola Plantio com Bulbinho']
  },
  {
    value: 'Banana',
    label: 'Banana',
    fenologia: 'banana',
    zarc: true,
    aliases: ['Banana Cavendish', 'Banana Maçã', 'Banana Prata'],
    observacao: 'ZARC varia por grupo e fase de implantação ou produção'
  },
  {
    value: 'Citros',
    label: 'Citros',
    fenologia: 'citros',
    zarc: false,
    aliases: ['Laranja', 'Lima', 'Limão', 'Tangerina', 'Pomelo', 'Toranja'],
    observacao: 'O ZARC publica espécies cítricas separadamente, sem categoria genérica'
  },
  {
    value: 'Uva',
    label: 'Uva',
    fenologia: 'uva',
    zarc: true,
    aliases: ['Uva Mesa', 'Uva Industrial'],
    observacao: 'ZARC disponível para uva de mesa e industrial'
  },
  {
    value: 'Cacau',
    label: 'Cacau',
    fenologia: 'cacau',
    zarc: true,
    aliases: ['Cacau Implantação', 'Cacau Produção', 'Cacau SAF'],
    observacao: 'ZARC varia entre implantação, produção e sistema agroflorestal'
  },
  {
    value: 'Braquiária',
    label: 'Braquiária',
    fenologia: 'braquiaria',
    zarc: false,
    aliases: ['Brachiaria', 'Urochloa'],
    observacao: 'Sem ZARC nominal isolado; há zoneamento para milho consorciado com braquiária'
  },
  {
    value: 'Capim Mombaça',
    label: 'Capim Mombaça',
    fenologia: 'capim_mombaca',
    zarc: false,
    aliases: ['Mombaça', 'Mombaca', 'Panicum maximum Mombaça', 'Megathyrsus maximus Mombaça'],
    observacao: 'Sem entrada nominal no painel ZARC'
  }
];

// ============================================================================
// FUNÇÕES DE MAPEAMENTO
// ============================================================================

function normalizarNomeCultura(culturaNome) {
  return String(culturaNome || '')
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[0-9ªº]/g, '')
    .replace(/\bsafra\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function nomesNormalizadosDaCultura(cultura) {
  return [cultura.value, ...(cultura.aliases || [])].map(normalizarNomeCultura);
}

function encontrarCultura(culturaNome) {
  const nomeNormalizado = normalizarNomeCultura(culturaNome);
  const correspondenciaExata = CULTURAS_DISPONIVEIS.find(cultura =>
    nomesNormalizadosDaCultura(cultura).includes(nomeNormalizado)
  );
  if (correspondenciaExata) return correspondenciaExata;

  return CULTURAS_DISPONIVEIS.find(cultura =>
    nomesNormalizadosDaCultura(cultura).some(candidato =>
      candidato.includes(nomeNormalizado) || nomeNormalizado.includes(candidato)
    )
  );
}

/**
 * Converte nome da cultura para chave de fenologia
 * @param {string} culturaNome - Nome da cultura (ex: "Soja", "Milho 1ª Safra", "SOJA")
 * @returns {string|null} - Chave da fenologia (ex: "soja", "milho") ou null
 */
export function culturaParaFenologia(culturaNome) {
  if (!culturaNome) return null;
  const cultura = encontrarCultura(culturaNome);
  
  return cultura?.fenologia || null;
}

/**
 * Converte nome da cultura para padrão ZARC (normalizado)
 * @param {string} culturaNome - Nome da cultura em qualquer formato
 * @returns {string} - Nome normalizado para busca no ZARC
 */
export function culturaParaZARC(culturaNome) {
  if (!culturaNome) return '';
  const cultura = encontrarCultura(culturaNome);
  
  return cultura?.value || culturaNome;
}

/**
 * Verifica se uma cultura tem dados ZARC disponíveis
 * @param {string} culturaNome - Nome da cultura
 * @returns {boolean}
 */
export function temDadosZARC(culturaNome) {
  if (!culturaNome) return false;
  const cultura = encontrarCultura(culturaNome);
  
  return cultura?.zarc === true;
}

/**
 * Verifica se uma cultura tem fenologia cadastrada
 * @param {string} culturaNome - Nome da cultura
 * @returns {boolean}
 */
export function temFenologia(culturaNome) {
  const chaveFenologia = culturaParaFenologia(culturaNome);
  return chaveFenologia !== null;
}

/**
 * Retorna informações completas sobre uma cultura
 * @param {string} culturaNome - Nome da cultura
 * @returns {Object|null}
 */
export function obterInfoCultura(culturaNome) {
  if (!culturaNome) return null;
  return encontrarCultura(culturaNome) || null;
}

/**
 * Lista todas as culturas com fenologia cadastrada
 * @returns {Array}
 */
export function culturasComFenologia() {
  return CULTURAS_DISPONIVEIS.filter(c => c.fenologia !== null);
}

/**
 * Lista todas as culturas com dados ZARC
 * @returns {Array}
 */
export function culturasComZARC() {
  return CULTURAS_DISPONIVEIS.filter(c => c.zarc === true);
}

// ============================================================================
// MAPEAMENTO LEGADO (para compatibilidade)
// ============================================================================

/**
 * Mapeamento antigo (lowercase) → novo (ZARC)
 * Use apenas para migração de dados antigos
 */
export const MAPEAMENTO_LEGADO = {
  'soja': 'Soja',
  'milho': 'Milho',
  'feijao': 'Feijão',
  'cafe': 'Café',
  'cafe_conilon': 'Café Conilon',
  'algodao': 'Algodão',
  'trigo': 'Trigo',
  'arroz': 'Arroz',
  'cana_de_acucar': 'Cana-de-açúcar',
  'sorgo': 'Sorgo',
  'girassol': 'Girassol',
  'amendoim': 'Amendoim',
  'mandioca': 'Mandioca',
  'eucalipto': 'Eucalipto',
  'tomate': 'Tomate',
  'batata': 'Batata',
  'cebola': 'Cebola',
  'banana': 'Banana',
  'citros': 'Citros',
  'uva': 'Uva',
  'cacau': 'Cacau',
  'braquiaria': 'Braquiária',
  'capim_mombaca': 'Capim Mombaça'
};

/**
 * Converte nome antigo para novo padrão
 * @param {string} culturaAntiga - Nome no formato antigo
 * @returns {string}
 */
export function migrarCulturaLegado(culturaAntiga) {
  if (!culturaAntiga) return '';
  
  const normalizado = culturaAntiga.toLowerCase().trim();
  return MAPEAMENTO_LEGADO[normalizado] || culturaAntiga;
}
